import React, { FunctionComponent, memo } from "react";
import { useTranslation } from "react-i18next";

import { DealerOrderPreview } from "../../../queries/usePreviewDealerOrderFromCart";
import {
  HashtagString,
  createHashStateHook,
} from "../../../stores/useHashState";
import { Dialog } from "../../../ui/Dialog/Dialog";
import { LinearProgress } from "../../../ui/LinearProgress";
import { NoData } from "../../NoData/NoData";
import { useOrderDetails } from "../useOrderDetails";
import { ButNowDisclaimer } from "./ButNowDisclaimer";
import { BuyNowForm } from "./BuyNowForm";
import { BuyNowModalHeader } from "./BuyNowModalHeader";
import { BuyNowOrderList } from "./BuyNowOrderList";
import { useNormalizedDealerOrder } from "./useNormalizedDealerOrder";

export const buyNowModalHash: HashtagString = "#buy-now";

export const useBuyNowModal = createHashStateHook(buyNowModalHash);

const Modal: FunctionComponent = () => {
  const { t } = useTranslation();
  const [visible, { hide }] = useBuyNowModal();
  const { data: orderDetails, isLoading, mutate } = useOrderDetails();
  const data = orderDetails as DealerOrderPreview | undefined;

  const normalizedData = useNormalizedDealerOrder(data);

  return (
    <Dialog
      className="p-4 md:p-6 lg:p-8 w-full"
      id="dealer-order-modal"
      onClose={hide}
      placement="top"
      visible={visible}
    >
      <BuyNowModalHeader close={hide} />
      {data && (
        <>
          <h2 className="md:text-lg font-semibold">
            {t("dealerOrderModal.itemInformation")}
          </h2>
        </>
      )}
      <div className="border border-dashed border-edge-2 rounded-tw-lg md:rounded-panel-section mt-2">
        {visible && isLoading && <LinearProgress className="h-[0.1rem] m-8" />}
        {!data && !isLoading && (
          <NoData className="m-4">{t("dealerOrderModal.noData")}</NoData>
        )}
        {data && !normalizedData && (
          <div className="px-4 md:px-8 py-4">
            {t("dealerOrderModal.errorMessage")}
          </div>
        )}
        {normalizedData && (
          <BuyNowOrderList revalidate={(): void => {
            void mutate();
          }} data={normalizedData} />
        )}
        {data && normalizedData && (
          <BuyNowForm
            revalidate={async (): Promise<void> => {
              await mutate();
            }}
            data={data}
            hide={hide}
            normalizedData={normalizedData}
          />
        )}
        <ButNowDisclaimer />
      </div>
    </Dialog>
  );
};

export const BuyNowModal = memo(Modal);
